import { Alert, type AlertRootProps } from '@chakra-ui/react/alert';
import { type ReactNode } from 'react';

import { type NotificationVariant } from '../../theme/common/AlertAndToast.common';
import { NotificationContent } from '../common/NotificationContent';
import { BUTTON_COLORS_MAP, ICON_COMPONENTS_MAP, type NotificationAction } from '../common/notificationMaps';

export type AlertVariants = NotificationVariant;
export type ActionProps = NotificationAction;

// Chakra v3's Alert.Root is a plain <div> with no ARIA semantics (v2's implicit role="alert" was
// dropped), so a dynamically shown alert wouldn't be announced. Default a live-region role by
// severity — only `critical` is assertive (interrupts); the rest are polite `status`. A live region
// only announces on dynamic insertion/change, so this is inert for alerts already present at load.
// Consumers override `role` (or any aria-* attribute) via `...rest`, which is spread last.
const ROLE_MAP: Record<NotificationVariant, 'alert' | 'status'> = {
  ai: 'status',
  critical: 'alert',
  info: 'status',
  progress: 'status',
  success: 'status',
  warning: 'status',
};

export interface BitkitAlertProps extends AlertRootProps {
  action?: ActionProps;
  dismissible?: boolean;
  messageText: ReactNode;
  onClose?: () => void;
  titleText?: ReactNode;
  variant: AlertVariants;
}

const BitkitAlert = (props: BitkitAlertProps) => {
  const { action, dismissible, messageText, onClose, titleText, variant, ...rest } = props;
  const IconComponent = ICON_COMPONENTS_MAP[variant];

  return (
    <Alert.Root role={ROLE_MAP[variant]} variant={variant} {...rest}>
      <Alert.Indicator asChild={variant !== 'progress'}>
        <IconComponent />
      </Alert.Indicator>
      <NotificationContent
        action={action}
        colorVariant={BUTTON_COLORS_MAP[variant]}
        dismissible={dismissible}
        messageText={messageText}
        onClose={onClose}
        titleText={titleText}
      />
    </Alert.Root>
  );
};

export default BitkitAlert;
